import React, { useRef } from 'react';

import MTableRowView from './MTableRow.view';
import { RowData } from '../Table.model';
import { Row, TableInstance } from 'react-table';
import classes from './MTableRow.module.scss';
import { DragSourceMonitor, useDrag, useDrop } from 'react-dnd';
import { DragItem, ItemType } from './MTableRow.model';

type Props<T extends RowData> = {
  table: TableInstance<T>;
  row: Row<T>;
  index: number;
  rowClassName?: string;
  selectedTable?: boolean;
  hiddenColumns: string[];
  hideSelection?: boolean;
  isDraggable?: boolean;
  columnCenter?: boolean;
  onRowClick?: (row: Row<T>) => void;
  isRowSelected: (row: Row<T>) => boolean;
  onMoveRow?: (fromIndex: number, toIndex: number) => void;
};

const MTableRow = <T extends RowData>(props: React.PropsWithChildren<Props<T>>): JSX.Element => {
  const rowRef = useRef(null);

  props.table.prepareRow(props.row);

  const rowClasses = [classes['row'], props.rowClassName];
  const selected = props.row.isSelected;
  const rowSelected = props.isRowSelected(props.row);

  if (selected || rowSelected) {
    rowClasses.push(classes['row--selected']);
  }

  if (rowSelected && props.selectedTable) {
    rowClasses.push(classes['row--selectedTableSelected']);
  }

  if (props.table.rows.length - 1 !== props.index) {
    rowClasses.push(classes['row--separate']);
  }

  if (!props.isDraggable) {
    rowClasses.push(classes['row--notDraggable']);
  }

  const [, drop] = useDrop<DragItem, unknown, unknown>({
    accept: 'ROW',
    hover(item: DragItem) {
      if (!rowRef.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = props.index;

      if (dragIndex === hoverIndex) {
        return;
      }

      props.onMoveRow?.(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { type: ItemType, index: props.index },
    canDrag: !!props.isDraggable,
    collect: (
      monitor: DragSourceMonitor<
        {
          type: string;
          index: number;
        },
        unknown
      >,
    ) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(rowRef));

  return (
    <MTableRowView
      rowRef={rowRef}
      hideSelection={props.hideSelection}
      onRowClick={props.onRowClick}
      hiddenColumns={props.hiddenColumns}
      rowSelected={rowSelected}
      id={props.row.id}
      row={props.row}
      rowClasses={rowClasses}
      table={props.table}
      isDragging={isDragging}
      columnCenter={props.columnCenter}
    >
      {props.children}
    </MTableRowView>
  );
};

MTableRow.displayName = 'MTableRow';
MTableRow.defaultProps = {};

export default MTableRow;
